import type { IFilter } from '@models/Filter'
import { endOfDay, startOfDay } from '@util/date-util'

import { apiClient } from '../../../gateways/api'
import type { ProjectenSearch } from '../types/ProjectenSearch'

export { initialState, createFilters, searchProjecten }

const initialState: ProjectenSearch = {
  data: { count: 0, skip: 0, limit: 25, items: [] },
  query: {
    filters: {
      active: true,
      processtap: [],
      netbeheerder: null,
      gebied: [],
      labels: [],
      myList: false,
      search: '',
      searchOption: null,
      fromWeek: '',
      toWeek: '',
      aannemer: null,
      typeWerk: [],
      gemeente: [],
      verantwoordelijke: null,
      contactpersoon: null,
      fromPlanningChanged: null,
      toPlanningChanged: null,
      planningsverschil: [],
      mijlpaal: null,
    },
    sort: { sortBy: 'aanvraagID', sortDirection: 'desc' },
    page: { skip: 0, limit: 25 },
  },
}

const createFilters = ({
  active,
  gebied,
  netbeheerder,
  processtap,
  labels,
  myList,
  search,
  searchOption,
  fromWeek,
  toWeek,
  aannemer,
  typeWerk,
  gemeente,
  verantwoordelijke,
  contactpersoon,
  fromPlanningChanged,
  toPlanningChanged,
  planningsverschil,
  mijlpaal,
}) => {
  const filters: IFilter[] = []

  if (active) {
    filters.push({
      name: 'isActive',
      value: true,
      operator: 'eq',
    })
  }

  if (gebied && gebied.length > 0)
    filters.push({
      name: 'gebiedscode',
      value: gebied.map(({ code }) => code),
      operator: 'in',
    })

  if (netbeheerder) {
    filters.push({
      name: 'netbeheerders',
      operator: 'eq',
      value: netbeheerder,
    })
  }

  if (processtap && processtap.length > 0) {
    filters.push({
      name: 'statusText',
      value: processtap,
      operator: 'in',
    })
  }

  if (labels && labels.length > 0)
    filters.push({
      name: 'labels',
      value: labels.map(({ code }) => code),
      operator: 'in',
    })

  if (myList) {
    filters.push({
      name: 'my-list',
      value: 'true',
      operator: 'eq',
    })
  }

  if (search && search !== '') {
    if (searchOption)
      filters.push({ name: searchOption.k, value: search, operator: 'eq' })
    else filters.push({ name: 'text', value: search, operator: 'contains' })
  }

  if (fromWeek)
    filters.push({
      name: 'startUitvoeringWeek',
      value: fromWeek,
      operator: 'gte',
    })
  if (toWeek)
    filters.push({
      name: 'startUitvoeringWeek',
      value: toWeek,
      operator: 'lte',
    })
  if (aannemer) {
    filters.push({
      name: 'aannemers',
      operator: 'eq',
      value: aannemer,
    })
  }
  if (Array.isArray(typeWerk) && typeWerk.length > 0) {
    filters.push({
      name: 'typeWerk',
      operator: 'in',
      value: typeWerk,
    })
  }
  if (Array.isArray(gemeente) && gemeente.length > 0) {
    filters.push({
      name: 'gemeente',
      operator: 'in',
      value: gemeente,
    })
  }
  if (verantwoordelijke) {
    filters.push({
      name: 'verantwoordelijken',
      operator: 'eq',
      value: verantwoordelijke.code,
    })
  }

  if (contactpersoon)
    filters.push({
      name: 'contactpersoon',
      operator: 'eq',
      value: contactpersoon.userid,
    })

  if (fromPlanningChanged)
    filters.push({
      name: 'planningChanged',
      value: startOfDay(fromPlanningChanged),
      operator: 'gte',
    })
  if (toPlanningChanged)
    filters.push({
      name: 'planningChanged',
      value: endOfDay(toPlanningChanged),
      operator: 'lte',
    })

  if (planningsverschil && planningsverschil.length > 0)
    filters.push({
      name: 'planningsverschillen',
      value: planningsverschil.map((v) => v.code),
      operator: 'in',
    })

  if (mijlpaal)
    filters.push({
      name: 'mijlpaal',
      value: mijlpaal,
      operator: 'eq',
    })

  return filters
}

const searchProjecten = async ({
  query,
}: {
  query: ProjectenSearch['query']
}): Promise<ProjectenSearch['data']> => {
  const { filters, page, sort } = query

  const { data } = await apiClient({
    url: '/rest/project/hoofdleiding/search',
    method: 'POST',
    data: { filters: createFilters(filters), ...page, ...sort },
  })

  return data
}
